import { FilterStateItem, ConnectProto } from "@racoon/api";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Namespace, useT } from "@racoon/i18n";

/** ---- constants ---- */
const getUserStates = (t: ReturnType<typeof useT>): FilterStateItem[] => [
  { key: t(Namespace.Employees, "tabs.all"), value: "all" },
  { key: t(Namespace.Employees, "tabs.dispatchers"), value: "dispatchers" },
  { key: t(Namespace.Employees, "tabs.drivers"), value: "drivers" },
  { key: t(Namespace.Employees, "tabs.mechanics"), value: "mechanics" },
  { key: t(Namespace.Employees, "tabs.support"), value: "support" },
  { key: t(Namespace.Employees, "tabs.management"), value: "management" },
  { key: t(Namespace.Employees, "tabs.sales"), value: "sales" },
  { key: t(Namespace.Employees, "tabs.client"), value: "client" },
  { key: t(Namespace.Employees, "tabs.audit"), value: "audit" },
  { key: t(Namespace.Employees, "tabs.archived"), value: "archived" },
];

const stateToRole: Record<string, ConnectProto.UserRole | undefined> = {
  dispatchers: ConnectProto.UserRole.PROVIDER_DISPATCHER,
  drivers: ConnectProto.UserRole.DRIVER,
  mechanics: ConnectProto.UserRole.PROVIDER_MECHANIC,
  support: ConnectProto.UserRole.SUPPORT,
  management: ConnectProto.UserRole.PROVIDER_OWNER,
  sales: ConnectProto.UserRole.PROVIDER_SALES,
  client: ConnectProto.UserRole.RECEIVER_USER,
  audit: ConnectProto.UserRole.GOVERNMENT_AUDITOR,
  archived: undefined,
  all: undefined,
};

const S = (v: unknown) => (v == null ? "" : String(v));

/** ---- options ---- */
type Options = {
  /** Raw array returned by API (for client filtering) */
  data?: ConnectProto.User[] | undefined;
  /** orgId → orgName map for search (ownerOrganization not populated in listUsers) */
  orgMap?: Map<string, string>;
};

export const useUsersUrlState = (opts: Options = {}) => {
  const [sp, setSp] = useSearchParams();
  const t = useT();

  const USER_STATES = useMemo<FilterStateItem[]>(() => getUserStates(t), [t]);

  /** ---------- BATCHED PATCHER (coalesces same-tick updates) ---------- */
  const pendingRef = useRef<URLSearchParams | null>(null);
  const flushIdRef = useRef<number | null>(null);

  const flushPatches = useCallback(() => {
    if (!pendingRef.current) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    flushIdRef.current = null;
    setSp(next, { replace: true });
  }, [setSp]);

  const patchMany = useCallback(
    (changes: Record<string, string | null>) => {
      const base = pendingRef.current ?? new URLSearchParams(sp);

      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === "") base.delete(k);
        else base.set(k, v);
      }

      pendingRef.current = base;

      if (flushIdRef.current == null) {
        flushIdRef.current = requestAnimationFrame(flushPatches);
      }
    },
    [sp, flushPatches],
  );

  // Cancel any pending flush on unmount so navigating away doesn't mutate the next page's URL.
  useEffect(() => {
    return () => {
      if (flushIdRef.current != null) {
        cancelAnimationFrame(flushIdRef.current);
        flushIdRef.current = null;
        pendingRef.current = null;
      }
    };
  }, []);

  const patch = useCallback((kv: Record<string, string | null>) => patchMany(kv), [patchMany]);

  // URL params (strings)
  const statusS = sp.get("status") ?? "all";
  const searchS = sp.get("search") ?? "";

  const selectedState = USER_STATES.find((s) => s.value === statusS) ?? USER_STATES[0];

  /** ----- URL-driven handlers ----- */
  const onStateChange = (v: FilterStateItem) => patch({ status: v.value });
  const onSearchChange = (value: string) => patch({ search: value });

  // -------------------------------------------------------------
  // CLIENT-SIDE FILTERING
  // -------------------------------------------------------------
  const { items, total, filteredTotal } = useMemo(() => {
    const all = opts.data ?? [];
    const totalAll = all.length;

    const matches = (user: ConnectProto.User) => {
      // 1. STATUS: hide archived users on all non-archived tabs
      const isArchivedTab = statusS === "archived";
      const userIsInactive = user.status === ConnectProto.UserStatus.INACTIVE;

      if (isArchivedTab) {
        if (!userIsInactive) return false;
      } else {
        if (userIsInactive) return false;
      }

      // 2. ROLE: tab filter
      if (statusS !== "all" && statusS !== "archived") {
        if (statusS === "management") {
          const isManager =
            user.role === ConnectProto.UserRole.PROVIDER_OWNER ||
            user.role === ConnectProto.UserRole.PROVIDER_OWNER_VIEW_ONLY;
          if (!isManager) return false;
        } else if (statusS === "audit") {
          const isAuditor =
            user.role === ConnectProto.UserRole.GOVERNMENT_AUDITOR ||
            user.role === ConnectProto.UserRole.GOVERNMENT_AUDITOR_EDITOR;
          if (!isAuditor) return false;
        } else {
          const requiredRole = stateToRole[statusS];
          if (requiredRole !== undefined && user.role !== requiredRole) {
            return false;
          }
        }
      }

      // 3. SEARCH
      if (searchS) {
        const q = searchS.toLowerCase();
        const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").toLowerCase();
        const orgName = (opts.orgMap?.get(String(user.ownerOrganizationId)) ?? "").toLowerCase();
        const phone = S(user.phoneNumber).toLowerCase();
        const email = S(user.email).toLowerCase();
        const driverLicense = S(user.driverLicenseNumber).toLowerCase();

        if (
          !fullName.includes(q) &&
          !orgName.includes(q) &&
          !phone.includes(q) &&
          !email.includes(q) &&
          !driverLicense.includes(q)
        )
          return false;
      }

      return true;
    };

    const filtered = all.filter(matches);
    return { items: filtered, total: totalAll, filteredTotal: filtered.length };
  }, [opts.data, opts.orgMap, searchS, statusS]);

  return {
    states: USER_STATES,
    selectedState,
    onStateChange,
    onSearchChange,
    searchValue: searchS,
    items,
    total,
    filteredTotal,
  };
};
